import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { 
  Menu, 
  X, 
  User, 
  Calendar, 
  MessageSquare, 
  Heart, 
  LogOut,
  Phone,
  Home,
  Pill,
  Sparkles,
  Star
} from 'lucide-react';

const Header = () => {
  const { user, isAuthenticated, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const navLinks = [
    { name: 'Home', href: '/', icon: Home },
    { name: 'Conditions We Treat', href: '/conditions', icon: Heart },
    { name: 'Beyond Urgent Care', href: '/programs', icon: Sparkles },
  ];

  const authNavLinks = [
    { name: 'Dashboard', href: '/dashboard', icon: Home },
    { name: 'Appointments', href: '/appointments', icon: Calendar },
    { name: 'Messages', href: '/messages', icon: MessageSquare },
    { name: 'Pharmacy', href: '/pharmacy', icon: Pill },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-sm shadow-lg border-b-2 border-[#1E3A8A]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 md:h-20">
          {/* Logo - Removed */}
          <div className="w-12"></div>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-1">
            {(isAuthenticated ? authNavLinks : navLinks).map((link) => (
              link.external ? (
                <a
                  key={link.name}
                  href={link.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="px-4 py-2 rounded-lg text-base font-black transition-colors text-slate-700 hover:text-[#1E3A8A] hover:bg-slate-100 text-center"
                  style={{ fontFamily: 'Merriweather, serif' }}
                >
                  {link.name}
                </a>
              ) : (
                <Link
                  key={link.name}
                  to={link.href}
                  className={`px-4 py-2 rounded-lg text-base font-black transition-colors text-center ${
                    isActive(link.href)
                      ? 'bg-[#1E3A8A]/10 text-[#1E3A8A]'
                      : 'text-slate-700 hover:text-[#1E3A8A] hover:bg-slate-100'
                  }`}
                  style={{ fontFamily: 'Merriweather, serif' }}
                >
                  {link.name}
                </Link>
              )
            ))}
          </nav>

          {/* Right side actions */}
          <div className="flex items-center gap-3">
            {/* At Home Test Kits Button */}
            <a
              href="https://homebloodkit.com/"
              target="_blank"
              rel="noopener noreferrer"
              className="hidden sm:inline-flex items-center justify-center gap-2 text-white font-black px-4 py-2 rounded-lg transition-all shadow-lg text-center"
              style={{ 
                background: 'linear-gradient(to right, #DC2626, #B91C1C)',
                fontFamily: 'Merriweather, serif'
              }}
              data-testid="home-test-kits-btn"
            >
              Shop At Home Test Kits
            </a>

            {/* Visit Integrated Wellness Button */}
            <a
              href="https://iwumm.com"
              target="_blank"
              rel="noopener noreferrer"
              className="hidden sm:inline-flex items-center justify-center gap-2 text-white font-black px-4 py-2 rounded-lg transition-all shadow-lg text-center"
              style={{ 
                background: 'linear-gradient(to right, #1E3A8A, #3B82F6)',
                fontFamily: 'Merriweather, serif'
              }}
              data-testid="visit-wellness-btn"
            >
              <Star className="w-4 h-4" />
              Visit Integrated Wellness
            </a>

            {/* Phone CTA */}
            <a
              href="tel:2015353582"
              className="hidden lg:flex items-center justify-center gap-2 text-base text-slate-700 hover:text-[#1E3A8A] transition-colors font-black text-center"
              style={{ fontFamily: 'Merriweather, serif' }}
            >
              <Phone className="w-4 h-4" />
              <span>(201) 535-3582</span>
            </a>

            {isAuthenticated ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button 
                    variant="ghost" 
                    className="flex items-center gap-2 rounded-lg border-2 border-[#1E3A8A]"
                    data-testid="user-menu-button"
                  >
                    <div className="w-8 h-8 rounded-full bg-[#1E3A8A]/10 flex items-center justify-center">
                      <User className="w-4 h-4 text-[#1E3A8A]" />
                    </div>
                    <span className="hidden sm:inline text-base font-black text-[#1E3A8A]" style={{ fontFamily: 'Merriweather, serif' }}>
                      {user?.first_name}
                    </span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56 border-2 border-[#1E3A8A]">
                  <div className="px-2 py-1.5">
                    <p className="text-base font-black text-[#1E3A8A]" style={{ fontFamily: 'Merriweather, serif' }}>{user?.first_name} {user?.last_name}</p>
                    <p className="text-sm text-slate-500 font-bold">{user?.email}</p>
                  </div>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem asChild>
                    <Link to="/dashboard" className="cursor-pointer font-bold">
                      <Home className="w-4 h-4 mr-2" />
                      Dashboard
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link to="/profile" className="cursor-pointer font-bold">
                      <User className="w-4 h-4 mr-2" />
                      Profile
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link to="/appointments" className="cursor-pointer font-bold">
                      <Calendar className="w-4 h-4 mr-2" />
                      Appointments
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem 
                    onClick={handleLogout}
                    className="cursor-pointer text-red-600 font-bold"
                  >
                    <LogOut className="w-4 h-4 mr-2" />
                    Log out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : null}

            {/* Mobile menu button */}
            <Button
              variant="ghost"
              size="icon"
              className="md:hidden"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="mobile-menu-button"
            >
              {mobileMenuOpen ? (
                <X className="w-5 h-5" />
              ) : (
                <Menu className="w-5 h-5" />
              )}
            </Button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {mobileMenuOpen && (
          <div className="md:hidden py-4 border-t-2 border-[#1E3A8A]">
            <nav className="flex flex-col gap-1">
              {(isAuthenticated ? authNavLinks : navLinks).map((link) => (
                link.external ? (
                  <a
                    key={link.name}
                    href={link.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => setMobileMenuOpen(false)}
                    className="flex items-center gap-3 px-4 py-3 rounded-lg text-base font-black transition-colors text-slate-700 hover:text-[#1E3A8A] hover:bg-slate-100"
                    style={{ fontFamily: 'Merriweather, serif' }}
                  >
                    <link.icon className="w-5 h-5" />
                    {link.name}
                  </a>
                ) : (
                  <Link
                    key={link.name}
                    to={link.href}
                    onClick={() => setMobileMenuOpen(false)}
                    className={`flex items-center gap-3 px-4 py-3 rounded-lg text-base font-black transition-colors ${
                      isActive(link.href)
                        ? 'bg-[#1E3A8A]/10 text-[#1E3A8A]'
                        : 'text-slate-700 hover:text-[#1E3A8A] hover:bg-slate-100'
                    }`}
                    style={{ fontFamily: 'Merriweather, serif' }}
                  >
                    <link.icon className="w-5 h-5" />
                    {link.name}
                  </Link>
                )
              ))}
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
